import { useRef, useState } from "react";
import Tilt from "react-parallax-tilt";
import { Plus, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Kinetic";
import Lightbox from "@/components/Lightbox";

const SIZES = ["S", "M", "L", "XL", "2XL"];
const FEE_SIZES = ["2XL"];
const SIZE_FEE = 30;

function ImageGallery({ images, alt, tall, productKey, onOpen }) {
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);

  const onScroll = () => {
    const el = trackRef.current;
    if (el) setIdx(Math.min(images.length - 1, Math.round(el.scrollLeft / el.clientWidth)));
  };
  const go = (dir) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" });
  };

  return (
    <div className="relative w-full">
      <div
        ref={trackRef}
        onScroll={onScroll}
        data-testid={`gallery-track-${productKey}`}
        className="flex overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {images.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={`${alt} — photo ${i + 1} of ${images.length}`}
            loading="lazy"
            draggable={false}
            onClick={() => onOpen?.(i)}
            className="w-full h-auto shrink-0 snap-center cursor-zoom-in transition-transform duration-[1200ms] ease-out group-hover:scale-[1.02]"
          />
        ))}
      </div>

      {images.length > 1 && (
        <>
          <button
            data-testid={`gallery-prev-${productKey}`}
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label="Previous photo"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/70 backdrop-blur-md border border-white/25 flex items-center justify-center text-white transition-colors duration-300 hover:bg-white hover:text-black z-10"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            data-testid={`gallery-next-${productKey}`}
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label="Next photo"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/70 backdrop-blur-md border border-white/25 flex items-center justify-center text-white transition-colors duration-300 hover:bg-white hover:text-black z-10"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {images.map((_, i) => (
              <span
                key={i}
                data-testid={`gallery-dot-${productKey}-${i}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-5 bg-white" : "w-1.5 bg-white/40"}`}
              />
            ))}
          </div>
          <span className="absolute top-4 right-4 text-[10px] font-unbounded bg-black/60 backdrop-blur-md border border-white/15 rounded-full px-3 py-1.5 text-white/80 z-10">
            {idx + 1}/{images.length} · swipe
          </span>
        </>
      )}
    </div>
  );
}

export default function ProductCard({ product, index, tall = false }) {
  const { add } = useCart();
  const oneSize = product.category === "accessory";
  const [size, setSize] = useState(oneSize ? "OS" : "M");
  const [added, setAdded] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const allImages = product.images?.length > 0 ? product.images : [product.image];

  const effectivePrice = product.price + (FEE_SIZES.includes(size) ? SIZE_FEE : 0);

  const handleAdd = () => {
    add({ ...product, price: effectivePrice }, size);
    setAdded(true);
    toast.success(`${product.name} — size ${size} added to cart`);
    setTimeout(() => setAdded(false), 1400);
  };

  return (
    <Reveal delay={(index % 4) * 0.08} className="w-full">
      <Tilt
        tiltMaxAngleX={7}
        tiltMaxAngleY={7}
        scale={1.015}
        transitionSpeed={1400}
        glareEnable
        glareMaxOpacity={0.12}
        glareColor="#ffffff"
        glarePosition="all"
        className="w-full"
      >
        <article data-testid={`product-card-${product.lookup_key}`} className="group">
          <div className="relative overflow-hidden rounded-sm shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)]">
            {product.images?.length >= 1 ? (
              <ImageGallery
                images={product.images}
                alt={`${product.name} — ${product.pieces}, ${product.process}`}
                tall={tall}
                productKey={product.lookup_key}
                onOpen={setLightboxIdx}
              />
            ) : (
              <img
                src={product.image}
                alt={`${product.name} — ${product.pieces}, ${product.process}`}
                loading="lazy"
                onClick={() => setLightboxIdx(0)}
                className={`w-full object-cover cursor-zoom-in transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06] ${tall ? "aspect-[3/4]" : "aspect-[362/912]"}`}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.25em] bg-black/60 backdrop-blur-md border border-white/15 rounded-full px-3.5 py-1.5 text-white/80">
              {product.process}
            </span>
            <span className="absolute bottom-4 right-4 font-unbounded text-2xl font-semibold opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
              ${effectivePrice}
            </span>
          </div>

          <div className="pt-6 flex items-start justify-between gap-4">
            <div>
              <p className="font-unbounded text-[10px] text-white/35 tracking-[0.3em] mb-2">
                {String(index + 1).padStart(2, "0")} — {product.pieces}
              </p>
              <h3 className="font-display font-medium text-xl sm:text-2xl tracking-tight">{product.name}</h3>
              <p className="text-sm text-white/50 mt-1.5 max-w-xs font-light">{product.blurb}</p>
              <p data-testid={`cost-note-${product.lookup_key}`} className="text-[10px] uppercase tracking-[0.18em] text-white/30 mt-3">
                {product.cost_note}
              </p>
            </div>
            <p className="font-unbounded text-lg whitespace-nowrap pt-1">${effectivePrice}</p>
          </div>

          <div className="flex items-center justify-between gap-3 mt-5">
            {oneSize ? (
              <p data-testid={`one-size-note-${product.lookup_key}`} className="text-[9px] uppercase tracking-[0.18em] text-white/35">
                One size fits all
              </p>
            ) : (
            <div className="flex flex-col gap-2">
              <div className="flex gap-1.5" role="group" aria-label={`Size for ${product.name}`}>
                {SIZES.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-pill-${product.lookup_key}-${s}`}
                    onClick={() => setSize(s)}
                    className={`text-[10px] tracking-widest px-2.5 py-1.5 rounded-full border transition-colors duration-300 ${
                      size === s ? "bg-white text-black border-white" : "border-white/20 text-white/60 hover:border-white/60"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <p data-testid={`size-fee-note-${product.lookup_key}`} className="text-[9px] uppercase tracking-[0.18em] text-white/35">
                2XL adds $30 — extended blanks cost more
              </p>
            </div>
            )}
            <button
              data-testid={`add-to-cart-${product.lookup_key}`}
              onClick={handleAdd}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] font-semibold transition-all duration-300 ${
                added ? "bg-[#ff1053] text-white" : "bg-white text-black hover:scale-105 active:scale-95"
              }`}
            >
              {added ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              {added ? "Added" : "Add"}
            </button>
          </div>
        </article>
      </Tilt>
      <Lightbox
        images={allImages}
        index={lightboxIdx}
        onClose={() => setLightboxIdx(null)}
        onNavigate={setLightboxIdx}
        alt={product.name}
      />
    </Reveal>
  );
}
